import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Constants from "../../../../Constants.json";
import IndexStrategiesPage from "./IndexStrategiesPage";
import { navigateToStockDashboard } from "../../../../utils/stockDashboardNav";
import {
  mergeStrategiesPagePayload,
  buildInvestorsMap,
  buildStratInvFromApi,
} from "./strategiesPageApiMapper";
import { STATIC_INVESTORS, STATIC_STRAT_INV } from "./strategiesStaticEnrich";

function apiBase() {
  const isLocalhost =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1");
  return isLocalhost
    ? "http://127.0.0.1:8000/api"
    : (Constants.BACKEND_SERVER_BASE_URL || "").replace(/\/$/, "");
}

async function fetchStrategiesPage(params = {}) {
  const base = apiBase();
  if (!base) throw new Error("API base URL not configured");
  const q = new URLSearchParams();
  if (params.strategy) q.set("strategy", params.strategy);
  if (params.investor) q.set("investor", params.investor);
  const suffix = q.toString() ? `?${q}` : "";
  const r = await fetch(`${base}/capitalflow/strategies${suffix}`);
  if (!r.ok) {
    const text = await r.text();
    throw new Error(`strategies ${r.status}: ${text.slice(0, 120)}`);
  }
  return r.json();
}

export default function IndexStrategiesPageContainer() {
  const navigate = useNavigate();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const loadedConstituents = useRef(new Set());
  const loadedInvestors = useRef(new Set());
  const pageDataRef = useRef(null);
  pageDataRef.current = pageData;

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    fetchStrategiesPage()
      .then((api) => {
        if (!mounted) return;
        const stratInv = buildStratInvFromApi(api);
        setPageData({
          allS: api.strategies || [],
          allComp: [],
          stratInv,
          investorsMap: buildInvestorsMap(
            { ...api, stratInv },
            STATIC_INVESTORS
          ),
        });
      })
      .catch((e) => {
        if (!mounted) return;
        setError(e.message);
        setPageData(null);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const mergeApi = useCallback((api) => {
    setPageData((prev) => mergeStrategiesPagePayload(prev, api));
  }, []);

  const onRequestConstituents = useCallback(
    async (strategyName) => {
      if (!strategyName) return 0;
      if (loadedConstituents.current.has(strategyName)) {
        return (
          pageDataRef.current?.allComp?.filter((c) => c.strategy === strategyName).length ?? 0
        );
      }
      try {
        const api = await fetchStrategiesPage({ strategy: strategyName });
        const n = (api.constituents || []).length;
        mergeApi(api);
        if (n > 0) loadedConstituents.current.add(strategyName);
        return n;
      } catch {
        loadedConstituents.current.delete(strategyName);
        return 0;
      }
    },
    [mergeApi]
  );

  const onRequestInvestor = useCallback(
    async (investorName) => {
      if (!investorName || loadedInvestors.current.has(investorName)) return;
      loadedInvestors.current.add(investorName);
      try {
        const api = await fetchStrategiesPage({ investor: investorName });
        mergeApi(api);
      } catch {
        loadedInvestors.current.delete(investorName);
      }
    },
    [mergeApi]
  );

  const onOpenTicker = useCallback(
    (sym) => navigateToStockDashboard(navigate, sym, "strategies"),
    [navigate]
  );

  return (
    <>
      {error && !pageData?.allS?.length && (
        <div
          style={{
            padding: "10px 16px",
            textAlign: "center",
            color: "#6f7b74",
            fontSize: 12,
            background: "rgba(202,168,94,.12)",
          }}
        >
          API indisponible ({error}) — affichage des données de démonstration.
        </div>
      )}
      <IndexStrategiesPage
        pageData={pageData}
        loading={loading}
        onRequestConstituents={onRequestConstituents}
        onRequestInvestor={onRequestInvestor}
        onOpenTicker={onOpenTicker}
      />
    </>
  );
}
